import { getBaggageEntryByName } from '@makemydeal/dr-platform-shared';
import { DXG_PUBLISHED_VERSION_BAGGAGE_NAME } from '@makemydeal/dr-sales-checkin-types';
import type { WorkflowInputs, DealStateSource } from '@makemydeal/dr-sales-checkin-types';
import type { PublishedVersionBaggageValue } from '@makemydeal/dr-shared-types';

/**
 * Flattens a deal record into the signals the showroom-state predicates read.
 *
 * This is the single adapter both sides go through — the BFF from the `DealXG` it just saved or
 * fetched, the client from its `activeDeal` slice — so the baggage lookup, the trade-in check and
 * the visibility/persona reads exist once rather than once per platform.
 *
 * Every field is defaulted, so a `undefined` deal, an empty object, or a record with no
 * `dealStructure` all produce the same all-negative signals rather than throwing. That matters on
 * the client, where rules run against a partially hydrated store.
 *
 * `isValid`, `missingFields` and `isDealershipAvailable` are passed separately because none of them
 * come from the record — the first two from deal validation, the third from dealership resolution.
 * All three default to the positive reading (valid, nothing missing, dealership resolved), matching
 * the client's unhydrated state and giving the client — which has no equivalent of
 * `isDealershipAvailable` at all — a harmless default rather than a false negative.
 */
export const getWorkflowInputs = (
    deal: DealStateSource | undefined,
    missingFields: string[] = [],
    isValid: boolean = true,
    isDealershipAvailable: boolean = true,
    isAmdPdfToggleEnabled: boolean = false
): WorkflowInputs => {
    const dealStructure = deal?.dealStructure;
    const visibility = dealStructure?.visibility;

    // Availability is the non-empty version, not the presence of the entry: the marker survives an
    // edit with `version: ''`, and it is that emptied marker which walks a desked deal back to
    // SENT_FOR_DESKING. An absent baggage list — a deal fetched without a baggage filter, or nothing
    // ever published — reads the same as unavailable.
    const publishedVersion = getBaggageEntryByName<PublishedVersionBaggageValue>(deal?.baggage, DXG_PUBLISHED_VERSION_BAGGAGE_NAME)
        ?.value?.content?.version;

    const isAmdOriginated = !!dealStructure?.versionCreatedBy?.applicationName?.includes('AMD');
    const wasEverAmdOriginated = Object.values(deal?.dealProgressMilestones ?? {}).some((milestone) =>
        milestone.applicationName?.includes('AMD')
    );

    return {
        hasPublishedVersion: !!publishedVersion,
        needsManagerApproval: !!visibility?.needsManagerApproval,
        approvedByManager: !!visibility?.approvedByManager,
        versionCreatedByPersona: dealStructure?.versionCreatedBy?.persona,
        isAmdOriginated,
        wasEverAmdOriginated,
        isAmdPdfToggleEnabled,
        acceptedByBuyer: !!dealStructure?.acceptedByBuyer,
        hasTradeIn: !!dealStructure?.vehicles?.tradeInVehicles?.tradeIns?.length,
        isValid,
        isDealershipAvailable,
        missingFields
    };
};
